import { useMutation, useQueryClient } from "@tanstack/react-query";
import { expiringRelatedPartiesKeys } from "Api/Queries/Party/useExpiringRelatedPartiesQuery";
import { getPartyQueryKey } from "Api/Queries/Party/usePartyQuery";
import { deletePartiesRelationsPublicID } from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

type Props = {
  /** Owning company whose related-parties list should be refreshed after deletion. */
  owningPartyPublicID?: string | null;
  onSuccess?: () => void;
  onError?: () => void;
};

/**
 * Deletes a single related-party relation row by its relation PublicID
 * (mirrors the update endpoint, which also keys off the relation PublicID).
 */
export const useDeletePartyRelationMutation = ({
  owningPartyPublicID,
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      (relationPublicID: string) =>
        deletePartiesRelationsPublicID(relationPublicID),
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getPartyQueryKey(owningPartyPublicID),
      });
      // Deleting a related party may clear a dashboard expiration warning, so
      // refresh the popup's source query too.
      await queryClient.invalidateQueries({
        queryKey: expiringRelatedPartiesKeys.all,
      });
      onSuccess?.();
    },
    onError,
  });
};
